import React, { useState, useMemo, useEffect } from "react";
import { Box, Checkbox, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import {
  GlassTextField,
  SectionHeader,
  GlassCard
} from '../../../components/glassmorphism/GlassmorphismStyles';

interface Category {
  key: string;
  name: string;
  subcategories: Category[];
}

interface CategoryTreeSelectProps {
  categories: Category[];
  toggleCategory: (selectedCategoryKey: string, isLeaf: boolean) => void;
  selectedCategoryKeys: Set<string>;
  isSingleSelect: boolean;
  isDisabledCheckboxes?: boolean;
  onlyLeafSelectable?: boolean; // New prop to enforce leaf-only selection
}

/** Recursively collect the keys of parent categories for a given key */
function findParentKeys(categories: Category[], targetKey: string, path: string[] = []): string[] {
  for (const category of categories) {
    const currentPath = [...path, category.key];
    if (category.key === targetKey) {
      return currentPath.slice(0, -1); // Exclude the target itself
    }

    if (category.subcategories.length > 0) {
      const result = findParentKeys(category.subcategories, targetKey, currentPath);
      if (result.length > 0) {
        return result;
      }
    }
  }
  return [];
}

/** Filter categories recursively */
function filterCategories(categories: Category[], searchText: string): Category[] {
  const lowerSearch = searchText.toLowerCase();

  return categories
    .map((cat) => {
      const filteredSubs = filterCategories(cat.subcategories, searchText);
      const nameMatches = cat.name.toLowerCase().includes(lowerSearch);

      if (nameMatches || filteredSubs.length > 0) {
        return { ...cat, subcategories: filteredSubs };
      }
      return null;
    })
    .filter(Boolean) as Category[];
}

const CategoryTreeSelect: React.FC<CategoryTreeSelectProps> = ({
  categories,
  toggleCategory,
  selectedCategoryKeys,
  isSingleSelect,
  isDisabledCheckboxes,
  onlyLeafSelectable = false, // Default to false for backward compatibility
}) => {
  const [searchText, setSearchText] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  /** Compute expanded keys on load or when selected categories change */
  useEffect(() => {
    setExpandedKeys((prevExpanded) => {
      const currentExpanded = new Set(prevExpanded);
      
      // Add parent keys of newly selected categories to the expanded set
      selectedCategoryKeys.forEach((key) => {
        const parents = findParentKeys(categories, key);
        parents.forEach((parentKey) => currentExpanded.add(parentKey));
      });
      
      return Array.from(currentExpanded);
    });
  }, [selectedCategoryKeys, categories]);

  /** Handle category toggle */
  const handleCategoryToggle = (categoryKey: string, isLeaf: boolean, hasSubcategories: boolean) => {
    console.log('🎯 CategoryTreeSelect: handleCategoryToggle called', { 
      categoryKey, 
      isLeaf, 
      hasSubcategories,
      onlyLeafSelectable 
    });
    
    if (onlyLeafSelectable && hasSubcategories) {
      console.log('📂 Parent category clicked, expanding instead of selecting');
      // If only leaf selection is enabled and this category has children, 
      // expand it instead of selecting
      setExpandedKeys((prev) => {
        if (prev.includes(categoryKey)) {
          // If already expanded, do nothing or collapse
          return prev;
        } else {
          // Expand this category to show its children
          return [...prev, categoryKey];
        }
      });
      return; // Don't call toggleCategory for parent nodes
    }
    
    // Call toggleCategory with isLeaf info
    console.log('✨ Calling parent toggleCategory function with:', { categoryKey, isLeaf });
    toggleCategory(categoryKey, isLeaf);
  };

  /** Render category tree */
  const renderCategoryTree = (categories: Category[]): React.ReactNode => {
    return categories.map((cat) => {
      const isChecked = selectedCategoryKeys.has(cat.key);
      const hasSubcategories = cat.subcategories.length > 0;
      const isLeaf = !hasSubcategories;
      const isDisabled = isDisabledCheckboxes || (onlyLeafSelectable && hasSubcategories);

      return (
        <TreeItem
          key={cat.key}
          itemId={cat.key}
          label={
            <Box 
              display="flex" 
              alignItems="center"
              onClick={(e) => {
                // If parent category and onlyLeafSelectable, expand on label click
                if (onlyLeafSelectable && hasSubcategories) {
                  e.stopPropagation();
                  setExpandedKeys((prev) => {
                    if (prev.includes(cat.key)) {
                      return prev.filter(k => k !== cat.key);
                    } else {
                      return [...prev, cat.key];
                    }
                  });
                }
              }}
              sx={{
                cursor: (onlyLeafSelectable && hasSubcategories) ? 'pointer' : 'default',
              }}
            >
              <Checkbox
                checked={isChecked}
                onChange={(e) => {
                  e.stopPropagation();
                  handleCategoryToggle(cat.key, isLeaf, hasSubcategories);
                }}
                size="small"
                sx={{ 
                  mr: 1,
                  color: 'rgba(0, 92, 173, 0.7)',
                  '&.Mui-checked': {
                    color: '#005CAD',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
                disabled={isDisabled}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: isDisabled ? '#999999' : isDisabledCheckboxes ? '#585858' : '#1A1A1A',
                  fontWeight: isChecked ? 500 : 400,
                  fontStyle: (onlyLeafSelectable && hasSubcategories) ? 'italic' : 'normal',
                }}
              >
                {cat.name}
                {onlyLeafSelectable && hasSubcategories && (
                  <Typography 
                    component="span" 
                    variant="caption" 
                    sx={{ 
                      ml: 1, 
                      color: '#666',
                      fontSize: '11px'
                    }}
                  >
                    (click to expand)
                  </Typography>
                )}
              </Typography>
            </Box>
          }
        >
          {hasSubcategories && renderCategoryTree(cat.subcategories)}
        </TreeItem>
      );
    });
  };

  // Filter the category tree for display
  const filteredCategories = useMemo(() => filterCategories(categories, searchText), [categories, searchText]);

  return (
    <GlassCard sx={{ p: 3 }}>
      <GlassTextField
        size="small"
        fullWidth
        placeholder="Search categories..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 3 }}
      />
      
      <Box 
        sx={{ 
          maxHeight: 350, 
          overflowY: "auto",
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 92, 173, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0, 92, 173, 0.5)',
            },
          },
          // Firefox scrollbar styling
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 92, 173, 0.3) rgba(255, 255, 255, 0.1)',
        }}
      >
        <SimpleTreeView
          multiSelect={!isSingleSelect}
          expandedItems={expandedKeys}
          onExpandedItemsChange={(e, newExpanded) => setExpandedKeys(newExpanded)}
          sx={{
            '& .MuiTreeItem-root': {
              '& .MuiTreeItem-content': {
                padding: '8px',
                borderRadius: '8px',
                margin: '2px 0',
                transition: 'all 0.2s ease',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 232, 231, 0.1)',
                  transform: 'translateX(4px)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 232, 231, 0.15)',
                },
              },
              '& .MuiTreeItem-label': {
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                color: '#1A1A1A',
              },
            },
          }}
        >
          {renderCategoryTree(filteredCategories)}
        </SimpleTreeView>
      </Box>
    </GlassCard>
  );
};

export default CategoryTreeSelect;
